import React, { useEffect, useMemo, useState } from "react";
import { format, isValid, parseISO } from "date-fns";
import { GlobalTableActionButton } from "../../../../../global/GlobalTableActionButton";
import GlobalBadge from "../../../../../global/GlobalBadge";
import GlobalDirectoryFiltersSection from "../../../../../global/directory/GlobalDirectoryFiltersSection";
import GlobalDirectoryGridSection from "../../../../../global/directory/GlobalDirectoryGridSection";
import {
  GlobalDirectoryShell,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
  GlobalDirectoryPaginationSection,
} from "../../../../../global/directory";

export const TO_AND_FRO_LIST_COLUMN_VISIBILITY_KEY =
  "girinestham.toFroDirectory.columnVisibility";

export const TO_AND_FRO_DIRECTORY_COLUMN_DEFS = [
  { id: "movement_code", label: "Movement ID", defaultVisible: true },
  { id: "movement_date", label: "Date", defaultVisible: true },
  { id: "staff_name", label: "Name", defaultVisible: true },
  { id: "designation", label: "Designation", defaultVisible: true },
  { id: "purpose_name", label: "Purpose", defaultVisible: true },
  { id: "place_visited", label: "Place visited", defaultVisible: true },
  { id: "out_time", label: "Out time", defaultVisible: true },
  { id: "in_time", label: "In time", defaultVisible: true },
  { id: "duration", label: "Duration", defaultVisible: true },
  { id: "status_name", label: "Status", defaultVisible: true },
  { id: "recorded_by", label: "Recorded by", defaultVisible: true },
];

/* "Duration" is derived; filter by it is ambiguous server-side — omit from column filter list only. */
const DIRECTORY_FILTER_COLUMNS = TO_AND_FRO_DIRECTORY_COLUMN_DEFS.filter((c) => c.id !== "duration");

/** Plain string for directory value dropdown (no JSX). */
function toFroFilterValueDisplay(col, row, masters) {
  switch (col) {
    case "movement_date":
      return formatDateCell(row.movement_date);
    case "out_time":
    case "in_time":
      return formatTimeCell(row[col]);
    case "staff_name": {
      const n = row.staff_name;
      if (n != null && String(n).trim() !== "") return String(n).trim();
      if (!masters?.users || !row.staff_user_id) return "";
      const staff = masters.users.find((u) => String(u.id) === String(row.staff_user_id));
      return staff?.name ? String(staff.name).trim() : "";
    }
    case "purpose_name": {
      const p = row.purpose_name;
      if (p != null && String(p).trim() !== "") return String(p).trim();
      if (!masters?.purposes || !row.purpose_id) return "";
      const pr = masters.purposes.find((x) => String(x.id) === String(row.purpose_id));
      return pr?.purpose_name ? String(pr.purpose_name).trim() : "";
    }
    case "movement_code":
      return String(row.movement_code ?? "").trim();
    case "recorded_by": {
      const n = row.created_by_name;
      if (n != null && String(n).trim() !== "") return String(n).trim();
      if (!masters?.users || !row.created_by) return "";
      const u = masters.users.find((x) => String(x.id) === String(row.created_by));
      return u?.name ? String(u.name).trim() : "";
    }
    default:
      return String(row[col] ?? "").trim();
  }
}

function loadToFroColumnVisibility(storageKey = TO_AND_FRO_LIST_COLUMN_VISIBILITY_KEY) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
}

export function createInitialToFroColumnVisibility(
  storageKey = TO_AND_FRO_LIST_COLUMN_VISIBILITY_KEY,
) {
  const saved = loadToFroColumnVisibility(storageKey);
  const initial = {};
  for (const c of TO_AND_FRO_DIRECTORY_COLUMN_DEFS) {
    initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
  }
  return initial;
}

function formatTimeCell(value) {
  if (value == null || value === "") return "—";
  try {
    const d = new Date(value);
    return isValid(d) ? format(d, "p") : "—";
  } catch {
    return "—";
  }
}

function formatDateCell(value) {
  if (value == null || value === "") return "—";
  try {
    const raw = String(value).trim();
    let d = parseISO(raw);
    if (!isValid(d) && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
      d = new Date(raw.replace(" ", "T"));
    }
    if (!isValid(d)) d = new Date(raw);
    return isValid(d) ? format(d, "PP") : "—";
  } catch {
    return "—";
  }
}

function formatDurationCell(outTime, inTime) {
  if (!outTime) return "—";
  try {
    const out = new Date(outTime);
    const inDateTime = inTime ? new Date(inTime) : new Date();
    const diff = inDateTime - out;
    if (diff < 0) return "Invalid";
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h${minutes}m`;
  } catch {
    return "—";
  }
}

export function toFroDirectoryCellValue(columnId, movement, masters) {
  switch (columnId) {
    case "movement_date":
      return formatDateCell(movement.movement_date);
    case "out_time":
    case "in_time":
      return formatTimeCell(movement[columnId]);
    case "duration":
      return formatDurationCell(movement.out_time, movement.in_time);
    case "staff_name": {
      const n = movement.staff_name;
      if (n != null && String(n).trim() !== "") return String(n).trim();
      if (!masters?.users || !movement.staff_user_id) return "—";
      const staff = masters.users.find((u) => String(u.id) === String(movement.staff_user_id));
      return staff?.name ?? "—";
    }
    case "purpose_name": {
      const p = movement.purpose_name;
      if (p != null && String(p).trim() !== "") return String(p).trim();
      if (!masters?.purposes || !movement.purpose_id) return "—";
      const purpose = masters.purposes.find((x) => String(x.id) === String(movement.purpose_id));
      return purpose?.purpose_name ?? "—";
    }
    case "movement_code":
      return movement.movement_code ?? "—";
    case "recorded_by": {
      const n = movement.created_by_name;
      if (n != null && String(n).trim() !== "") return String(n).trim();
      if (!masters?.users || !movement.created_by) return "—";
      const u = masters.users.find((x) => String(x.id) === String(movement.created_by));
      return u?.name ?? "—";
    }
    default:
      return movement[columnId] ?? "—";
  }
}

export function toFroDefaultDirectoryFilters() {
  return {
    user_search: "",
    column: "",
    value: "",
  };
}

function uniqSelectOptions(...groups) {
  const seen = new Set();
  const out = [];
  for (const opts of groups) {
    for (const o of opts) {
      if (!o) continue;
      const v = String(o.value ?? "").trim();
      if (!v || seen.has(v)) continue;
      seen.add(v);
      const label = String(o.label ?? v).trim() || v;
      out.push({ value: v, label });
    }
  }
  return out.sort((a, b) =>
    a.label.localeCompare(b.label, undefined, {
      numeric: true,
      sensitivity: "base",
    }),
  );
}

function movementDateIso(movement) {
  const raw = movement?.movement_date || movement?.out_time;
  if (raw == null || raw === "") return "";
  try {
    const s = String(raw).trim();
    let d = parseISO(s);
    if (!isValid(d)) d = new Date(s);
    if (!isValid(d)) return "";
    return format(d, "yyyy-MM-dd");
  } catch {
    return "";
  }
}

function ToAndFroDirectoryFilters({
  draftFilters,
  onDraftChange,
  onClear,
  movements,
  masters,
  fetchDirectoryFilterOptions,
  secondaryStart,
  secondaryEnd,
}) {
  const [apiFilterOptions, setApiFilterOptions] = useState([]);
  const [loadingFilterOptions, setLoadingFilterOptions] = useState(false);

  useEffect(() => {
    const col = draftFilters.column;
    if (!col || typeof fetchDirectoryFilterOptions !== "function") {
      setApiFilterOptions([]);
      setLoadingFilterOptions(false);
      return;
    }
    let cancelled = false;
    setLoadingFilterOptions(true);
    fetchDirectoryFilterOptions(col)
      .then((opts) => {
        if (!cancelled) setApiFilterOptions(Array.isArray(opts) ? opts : []);
      })
      .catch(() => {
        if (!cancelled) setApiFilterOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingFilterOptions(false);
      });
    return () => {
      cancelled = true;
    };
  }, [draftFilters.column, fetchDirectoryFilterOptions]);

  const valueFilterOptions = useMemo(() => {
    const col = draftFilters.column;
    if (!col) return [];
    if (typeof fetchDirectoryFilterOptions === "function") {
      return [...apiFilterOptions].sort((a, b) =>
        String(a.label ?? a.value).localeCompare(String(b.label ?? b.value), undefined, {
          numeric: true,
          sensitivity: "base",
        }),
      );
    }
    if (col === "purpose_name") {
      const fromLookup = (masters?.purposes || []).map((p) => {
        const n = String(p.purpose_name ?? "").trim();
        return n ? { value: n, label: n } : null;
      });
      const fromRows = [];
      for (const row of movements) {
        const n = row.purpose_name;
        if (n == null || String(n).trim() === "") continue;
        const v = String(n).trim();
        fromRows.push({ value: v, label: v });
      }
      return uniqSelectOptions(fromLookup.filter(Boolean), fromRows);
    }
    if (col === "status_name") {
      const fromLookup = (masters?.statuses || []).map((s) => {
        const n = String(s.status_name ?? "").trim();
        return n ? { value: n, label: n } : null;
      });
      const fromRows = [];
      for (const row of movements) {
        const n = row.status_name;
        if (n == null || String(n).trim() === "") continue;
        const v = String(n).trim();
        fromRows.push({ value: v, label: v });
      }
      return uniqSelectOptions(fromLookup.filter(Boolean), fromRows);
    }
    if (col === "recorded_by") {
      const fromLookup = (masters?.users || []).map((u) => {
        const n = String(u.name ?? "").trim();
        if (!n) return null;
        return { value: String(u.id), label: n };
      });
      const fromRows = [];
      for (const row of movements) {
        if (row.created_by == null || String(row.created_by).trim() === "") continue;
        const label =
          row.created_by_name && String(row.created_by_name).trim()
            ? String(row.created_by_name).trim()
            : toFroFilterValueDisplay("recorded_by", row, masters);
        if (!label) continue;
        fromRows.push({ value: String(row.created_by), label });
      }
      return uniqSelectOptions(fromLookup.filter(Boolean), fromRows);
    }
    if (col === "movement_date") {
      const seen = new Set();
      const opts = [];
      for (const row of movements) {
        const iso = movementDateIso(row);
        if (!iso || seen.has(iso)) continue;
        seen.add(iso);
        opts.push({ value: iso, label: formatDateCell(row.movement_date || row.out_time) });
      }
      return opts.sort((a, b) => a.value.localeCompare(b.value));
    }
    const seen = new Set();
    const opts = [];
    for (const row of movements) {
      const label = toFroFilterValueDisplay(col, row, masters);
      if (!label) continue;
      if (!seen.has(label)) {
        seen.add(label);
        opts.push({ value: label, label });
      }
    }
    return opts.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, {
        numeric: true,
        sensitivity: "base",
      }),
    );
  }, [
    draftFilters.column,
    movements,
    masters,
    fetchDirectoryFilterOptions,
    apiFilterOptions,
  ]);

  return (
    <GlobalDirectoryFiltersSection
      className="ub-filters ub-tf-directory-filters"
      searchLabel="Search by username"
      searchPlaceholder="Type a name"
      searchValue={draftFilters.user_search}
      onSearchChange={(v) => onDraftChange({ ...draftFilters, user_search: v })}
      columnLabel="Column"
      columnValue={draftFilters.column}
      onColumnChange={(v) => onDraftChange({ ...draftFilters, column: v, value: "" })}
      gridColumnDefs={DIRECTORY_FILTER_COLUMNS}
      columnPlaceholderOption="Select"
      valueLabel="Value"
      valueValue={draftFilters.value}
      onValueChange={(v) => onDraftChange({ ...draftFilters, value: v })}
      valueOptions={valueFilterOptions}
      valuePlaceholderOption="All"
      valueSelectDisabled={typeof fetchDirectoryFilterOptions === "function" && loadingFilterOptions}
      onClear={onClear}
      clearFilterLabel="Clear filter"
      secondaryStart={secondaryStart}
      secondaryEnd={secondaryEnd}
    />
  );
}

function ToAndFroMovementsTable({
  movements,
  masters,
  columnVisibility,
  loading,
  onView,
  onEdit,
  onReturn,
  onDelete,
}) {
  const visibleDataColumns = useMemo(
    () => TO_AND_FRO_DIRECTORY_COLUMN_DEFS.filter((c) => columnVisibility[c.id]),
    [columnVisibility],
  );

  const tableHeaders = useMemo(
    () => [
      ...visibleDataColumns.map((c) => ({ key: c.id, label: c.label })),
      {
        key: "actions",
        label: "Actions",
        className: "ub-table__col-actions",
      },
    ],
    [visibleDataColumns],
  );

  const showRows = !loading && movements.length > 0;

  return (
    <GlobalDirectoryGridSection
      loading={loading}
      loadingMessage="Loading movements…"
      headers={tableHeaders}
      isEmpty={!loading && movements.length === 0}
      emptyMessage="No movements found."
      tableAriaLabel="To and Fro movements directory"
      wrapClassName="ub-table-wrap"
      tableClassName="ub-table"
    >
      {showRows
        ? movements.map((movement) => (
            <tr key={movement.id}>
              {visibleDataColumns.map((col) => (
                <td key={col.id}>
                  {col.id === "status_name" ? (
                    <GlobalBadge
                      active={movement.status_id === 2}
                      activeText={movement.status_name || "Returned"}
                      inactiveText={movement.status_name || "Out"}
                    />
                  ) : col.id === "movement_code" ? (
                    <span style={{ fontWeight: 600, fontFamily: "monospace" }}>
                      {toFroDirectoryCellValue(col.id, movement, masters)}
                    </span>
                  ) : (
                    toFroDirectoryCellValue(col.id, movement, masters)
                  )}
                </td>
              ))}
              <td className="ub-table__col-actions">
                <div className="ub-table-actions">
                  <GlobalTableActionButton
                    action="view"
                    title="View movement details"
                    onClick={() => onView?.(movement)}
                  />
                  {onEdit ? (
                    <GlobalTableActionButton
                      action="edit"
                      title="Edit movement"
                      onClick={() => onEdit(movement)}
                    />
                  ) : null}
                  {onReturn && movement.status_id === 1 ? (
                    <GlobalTableActionButton
                      action="return"
                      onClick={() => onReturn(movement)}
                    />
                  ) : null}
                  {onDelete ? (
                    <GlobalTableActionButton
                      action="delete"
                      title="Delete movement"
                      onClick={() => onDelete(movement)}
                    />
                  ) : null}
                </div>
              </td>
            </tr>
          ))
        : null}
    </GlobalDirectoryGridSection>
  );
}

export default function ToAndFroDirectorySection({
  movements,
  masters,
  fetchDirectoryFilterOptions,
  draftDirectoryFilters,
  onDraftDirectoryFiltersChange,
  onClearDirectoryFilters,
  columnVisibility,
  onColumnVisibilityChange,
  getToAndFroExportData,
  loadingMovements,
  directoryPage,
  directoryTotalPages,
  directoryTotal,
  directoryPageSize,
  onDirectoryPageChange,
  onView,
  onEdit,
  onReturn,
  onDelete,
  sectionDomId = "tf-list",
  columnVisibilityStorageKey = TO_AND_FRO_LIST_COLUMN_VISIBILITY_KEY,
  directoryActionMode = "admin",
  directoryPaginationLabel = "To and Fro directory pagination",
}) {
  return (
    <div
      id={sectionDomId}
      className="user-builder-panel"
      style={{ scrollMarginTop: "2rem" }}
    >
      <div className="user-builder-panel__head">
        <h3 className="user-builder-panel__title">Directory</h3>
        <p className="user-builder-panel__hint">
          Search and manage staff and volunteer movement records.
        </p>
      </div>
      <GlobalDirectoryShell
        filters={
          <ToAndFroDirectoryFilters
            draftFilters={draftDirectoryFilters}
            onDraftChange={onDraftDirectoryFiltersChange}
            onClear={onClearDirectoryFilters}
            movements={movements}
            masters={masters}
            fetchDirectoryFilterOptions={fetchDirectoryFilterOptions}
            secondaryStart={
              <GlobalDirectoryColumnPicker
                columnDefs={TO_AND_FRO_DIRECTORY_COLUMN_DEFS}
                visibility={columnVisibility}
                onVisibilityChange={onColumnVisibilityChange}
                storageKey={columnVisibilityStorageKey}
              />
            }
            secondaryEnd={
              getToAndFroExportData ? (
                <GlobalDirectoryExportMenu
                  getExportData={getToAndFroExportData}
                  filenameBase="to_and_fro_directory"
                />
              ) : null
            }
          />
        }
        approvals={null}
        grid={
          <ToAndFroMovementsTable
            movements={movements}
            masters={masters}
            columnVisibility={columnVisibility}
            loading={loadingMovements}
            onView={onView}
            onEdit={onEdit}
            onReturn={onReturn}
            onDelete={onDelete}
          />
        }
        pagination={
          <GlobalDirectoryPaginationSection
            page={directoryPage}
            totalPages={directoryTotalPages}
            total={directoryTotal}
            pageSize={directoryPageSize}
            onPageChange={onDirectoryPageChange}
            label={directoryPaginationLabel}
          />
        }
      />
    </div>
  );
}
